import type { DealResponseDto } from "../../../types/types";

export interface AggregatedComponent {
  componentType: string;
  calculationMode?: string;
  resolvedAmount: number;
  componentLabel?: string;
}

/**
 * The chat service attaches the SAME pre/post match breakdown lists to every
 * component payment, so "first value wins per componentType" is a deliberate
 * de-duplication. Do NOT sum the values: that would multiply the amounts.
 * Known edge case: two components with the same componentType collapse into one.
 */
export const getAggregatedComponents = (
  deal: DealResponseDto,
): AggregatedComponent[] => {
  const componentPayments = deal.componentPayments as any[] | undefined;
  if (!componentPayments || componentPayments.length === 0) return [];

  const byType: Record<string, AggregatedComponent> = {};

  componentPayments.forEach((payment) => {
    [
      ...(payment.preMatchBreakdown || []),
      ...(payment.postMatchBreakdown || []),
    ].forEach((item: any) => {
      const key = item.componentType;
      if (!byType[key]) {
        byType[key] = {
          componentType: key,
          calculationMode: item.calculationMode,
          resolvedAmount: item.resolvedAmount ?? 0,
          componentLabel: item.componentLabel ?? item.label ?? item.displayName,
        };
      }
    });
  });

  return Object.values(byType);
};

/** Base amount first, the rest in their original order. */
export const sortComponents = (
  components: AggregatedComponent[],
): AggregatedComponent[] =>
  [...components].sort((a, b) => {
    if (a.componentType === "BASE_AMOUNT") return -1;
    if (b.componentType === "BASE_AMOUNT") return 1;
    return 0;
  });

/** "BASE_AMOUNT" / "baseAmount" -> "Base amount" (sentence case). */
export const formatComponentLabel = (value?: string | null): string => {
  const words = String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export type StatusTone = "neutral" | "success" | "warning" | "danger";

const TONE_CLASSES: Record<StatusTone, string> = {
  neutral: "bg-gray-100 text-gray-600",
  success: "bg-green-50 text-green-700",
  warning: "bg-amber-50 text-amber-700",
  danger: "bg-red-50 text-red-700",
};

const TONE_BY_STATUS: Record<string, StatusTone> = {
  PROPOSED: "warning",
  WAITING_FOR_RESULT: "warning",
  RESULT_UPDATED: "warning",
  POST_PAYMENT: "warning",
  DISPUTE: "warning",
  PAYMENT_REFUNDED: "warning",
  ACCEPTED: "success",
  CONFIRMED: "success",
  RESULT_CONFIRMED: "success",
  PRE_PAYMENT_RELEASED: "success",
  POST_PAYMENT_RELEASED: "success",
  FINISHED: "success",
  FIGHTER_RATED: "success",
  MANAGER_RATED: "success",
  REJECTED: "danger",
  EXPIRED: "danger",
  CANCELLED: "neutral",
  CLOSED: "neutral",
};

/** Tailwind classes for the status chip. Unknown statuses are neutral. */
export const getStatusConfig = (status: string): { classes: string } => ({
  classes: TONE_CLASSES[TONE_BY_STATUS[status] ?? "neutral"],
});

/** Location can be a plain string or { city, venue, country }. */
export const getLocationString = (location: unknown): string => {
  if (location && typeof location === "object") {
    const { city, venue, country } = location as Record<string, unknown>;
    return [city, venue, country].filter(Boolean).join(", ");
  }
  return String(location || "");
};

/** "4,000 €": number, space, currency symbol (falls back to the currency code). */
export const formatMoney = (
  amount: number,
  currency: string | undefined,
  lang: string,
): string => {
  try {
    const value = new Intl.NumberFormat(lang).format(amount);
    if (!currency) return value;

    let symbol = currency;
    try {
      const parts = new Intl.NumberFormat(lang, {
        style: "currency",
        currency,
      }).formatToParts(0);
      symbol = parts.find((part) => part.type === "currency")?.value ?? currency;
    } catch {
      // Not a valid ISO currency code: keep the code as the symbol.
    }
    return `${value} ${symbol}`;
  } catch {
    return `${amount}${currency ? ` ${currency}` : ""}`;
  }
};

/** "28 Aug 2026" (English uses day-first en-GB, as in the design). Plain dates are parsed as local dates to avoid timezone shifts. */
export const formatEventDate = (value: unknown, lang: string): string => {
  if (!value) return "";
  const text = String(value);

  const plain = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = plain
    ? new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
    : new Date(text);

  if (Number.isNaN(date.getTime())) return text;

  try {
    return new Intl.DateTimeFormat(lang.startsWith("en") ? "en-GB" : lang, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return text;
  }
};

/**
 * The chat DTO has no eventResponse, so the name is only shown if it happens to
 * be in formData. Returns null (row hidden) when nothing is found.
 */
export const getEventName = (deal: DealResponseDto): string | null => {
  const candidate =
    deal.eventResponse?.eventName ??
    deal.formData?.details?.eventName ??
    deal.formData?.eventName;
  return typeof candidate === "string" && candidate.trim() ? candidate : null;
};

/** The fighter the deal was proposed to can act while it is PROPOSED. */
export const canFighterActOnDeal = (
  deal: DealResponseDto,
  currentUserId?: string | number,
): boolean =>
  deal.status === "PROPOSED" &&
  currentUserId !== undefined &&
  deal.acceptedBy?.id === currentUserId;

/** Only the manager who created a PROPOSED deal can edit or withdraw it. */
export const canManagerWithdrawDeal = (
  deal: DealResponseDto,
  currentUserId: string | number | undefined,
  userRole: string | undefined,
): boolean =>
  userRole === "MANAGER" &&
  deal.status === "PROPOSED" &&
  currentUserId !== undefined &&
  deal.initiatedBy?.id === currentUserId;
